import { useCallback, useContext } from 'react';
import { MapContext } from '../MapContext';
import { MapMouseEvent } from 'maplibre-gl';
import { useKineticMap } from './useKineticMap';
import { DEFAULT_ZOOM } from 'kineticUtils/hooks';
import { IconLayer } from 'deck.gl';
import { useBoolean } from '@qfa/react-components';
import mapPinRed from '@qfa/react-components/lib/components/images/mapPinRed.png';
import bbox from '@turf/bbox';
import { BBox2d, Position } from '@turf/helpers/dist/js/lib/geojson';
import { isArray } from 'lodash';

const LOCATION_PIN_ID = 'searchLocationPinId';
export type GeometryData = { coordinates: GeoJSON.Position | Position; type: string };

export const useMapSearchBoxPin = () => {
  const map = useKineticMap();
  const { setExtraDeckGlLayers } = useContext(MapContext);
  const [{ on: locationSelected, off: removeLocation }, isLocationSelected] = useBoolean();

  const addMapPin = useCallback(
    (geometry: GeometryData) => {
      const bounds = (geometry ? bbox(geometry) : [0, 0, 0, 0]) as BBox2d;
      setExtraDeckGlLayers((prev) => {
        const toSave = [...prev];
        const layer = new IconLayer<GeometryData>({
          id: LOCATION_PIN_ID,
          data: [geometry],
          getIcon: () => ({
            url: mapPinRed,
            width: 24,
            height: 24,
          }),
          getPixelOffset: [0, 0],
          getPosition: (d: { coordinates: any }) => {
            if (isArray(d.coordinates[0])) {
              return [
                bounds[0] + (bounds[2] - bounds[0]) / 2,
                bounds[1] + (bounds[3] - bounds[1]) / 2,
              ];
            }
            return [d.coordinates[0], d.coordinates[1]];
          },
          getSize: 40,
          pickable: false,
        });
        const index = toSave.findIndex((layer) => layer.id === LOCATION_PIN_ID);
        if (index > -1) {
          toSave.splice(index, 1);
        }
        toSave.push(layer as any);
        return toSave;
      });
      locationSelected();
      map.fitBounds([
        [bounds[0], bounds[1]],
        [bounds[2], bounds[3]],
      ]);
      map.setZoom(DEFAULT_ZOOM);
    },
    [locationSelected, map, setExtraDeckGlLayers]
  );

  const removeMapPin = useCallback(() => {
    isLocationSelected &&
      setExtraDeckGlLayers((prev) => {
        const toSave = [...prev];
        const index = toSave.findIndex((layer) => layer.id === LOCATION_PIN_ID);
        if (index > -1) {
          toSave.splice(index, 1);
        }
        removeLocation();
        return toSave;
      });
  }, [isLocationSelected, removeLocation, setExtraDeckGlLayers]);

  return { addMapPin, removeMapPin };
};

export interface useMapSearchBoxPinProps {
  onClick?: (event: MapMouseEvent) => void;
  onContextMenu?: (event: MapMouseEvent) => void;
}

export default useMapSearchBoxPin;
